import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { AlertService } from 'src/app/services/alert/alert.service';
import { CandidatoCvService } from 'src/app/services/candidato-cv/candidato-cv.service';
import { ConsentService } from 'src/app/services/consent/consent.service';
import { ScrapedJobService } from 'src/app/services/scraped-job/scraped-job.service';

interface MatchItem {
  score: number;
  titulo: string;
  empresa: string;
  ubicacion: string;
  modalidad: string;
  urlVacante: string;
}

type Step = 'checking' | 'phone' | 'otp' | 'cv' | 'processing' | 'results' | 'error';

@Component({
  selector: 'app-free-onboarding',
  templateUrl: './free-onboarding.component.html'
})
export class FreeOnboardingComponent implements OnInit {
  step: Step = 'checking';

  // Phone
  selectedDialCode = '521';
  phoneNumber = '';
  phoneError = '';

  // OTP
  otpCode = '';
  sendingOtp = false;
  verifyingOtp = false;
  otpError = '';

  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  // CV Upload
  uploadingCv = false;
  cvUploadError = '';

  // Results
  matches: MatchItem[] = [];
  sent = false;
  channel = 'NONE';
  errorTitle = '';
  errorMsg = '';
  errorIcon = '';

  readonly countries = [
    { dial: '521', display: '52', flag: '🇲🇽' },
    { dial: '1',   display: '1',  flag: '🇺🇸' },
    { dial: '57',  display: '57', flag: '🇨🇴' },
    { dial: '54',  display: '54', flag: '🇦🇷' },
    { dial: '56',  display: '56', flag: '🇨🇱' },
    { dial: '51',  display: '51', flag: '🇵🇪' },
    { dial: '593', display: '593', flag: '🇪🇨' },
    { dial: '58',  display: '58', flag: '🇻🇪' },
    { dial: '598', display: '598', flag: '🇺🇾' },
    { dial: '34',  display: '34', flag: '🇪🇸' },
  ];

  constructor(
    private alertService: AlertService,
    private cvService: CandidatoCvService,
    private consent: ConsentService,
    private scrapedJobService: ScrapedJobService,
    private router: Router
  ) {}

  get fullPhone(): string {
    const digits = this.phoneNumber.replace(/\D/g, '');
    return this.selectedDialCode + digits;
  }

  ngOnInit() {
    this.consent.syncFromServer().subscribe();
    this.checkStatus();
  }

  private getCandidatoId(): number | null {
    const raw = localStorage.getItem('currentUser');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed?.user?.idUsuario ?? null;
    } catch {
      return null;
    }
  }

  private checkStatus() {
    const candidatoId = this.getCandidatoId();
    if (!candidatoId) {
      this.router.navigate(['/acceder']);
      return;
    }
    this.alertService.getEstado(candidatoId).subscribe({
      next: (res: any) => {
        const data = res?.data || res;
        if (data?.whatsappOptIn && data?.whatsappPhoneSet) {
          this.checkCvAndProceed();
        } else {
          this.step = 'phone';
        }
      },
      error: () => {
        this.step = 'phone';
      }
    });
  }

  sendOtp() {
    const digits = this.phoneNumber.replace(/\D/g, '');
    if (digits.length < 7) {
      this.phoneError = 'Ingresa un número válido';
      return;
    }
    this.phoneError = '';
    this.sendingOtp = true;

    const candidatoId = this.getCandidatoId();
    this.alertService.sendOtp(this.fullPhone, candidatoId).subscribe({
      next: () => {
        this.sendingOtp = false;
        this.step = 'otp';
        this.otpError = '';
      },
      error: (err) => {
        this.sendingOtp = false;
        this.phoneError = err?.error?.message || err?.message || 'Error al enviar código';
      }
    });
  }

  verifyOtp() {
    const code = this.otpCode.trim();
    if (code.length < 6) {
      this.otpError = 'Ingresa el código completo de 6 dígitos';
      return;
    }
    this.otpError = '';
    this.verifyingOtp = true;

    const candidatoId = this.getCandidatoId();
    this.alertService.verifyOtp(this.fullPhone, code, candidatoId).subscribe({
      next: () => {
        this.verifyingOtp = false;
        this.checkCvAndProceed();
      },
      error: (err) => {
        this.verifyingOtp = false;
        const backendMsg = err?.error?.message || err?.message || '';
        // Mostrar el mensaje real del backend, no uno genérico
        if (backendMsg.includes('ya está verificado en otra cuenta')) {
          this.otpError = 'Este número de WhatsApp ya está asociado a otra cuenta. Si es tu número, contacta a soporte.';
        } else if (backendMsg.includes('expirado')) {
          this.otpError = 'El código expiró. Solicita uno nuevo.';
        } else if (backendMsg) {
          this.otpError = backendMsg;
        } else {
          this.otpError = 'Código incorrecto. Solicita uno nuevo.';
        }
        this.otpCode = '';
      }
    });
  }

  resendOtp() {
    this.otpCode = '';
    this.otpError = '';
    this.sendingOtp = true;
    const candidatoId = this.getCandidatoId();
    this.alertService.sendOtp(this.fullPhone, candidatoId).subscribe({
      next: () => {
        this.sendingOtp = false;
      },
      error: (err) => {
        this.sendingOtp = false;
        this.otpError = err?.error?.message || 'Error al reenviar código';
      }
    });
  }

  dismissPrompt() {
    const candidatoId = this.getCandidatoId();
    if (!candidatoId) { this.goHome(); return; }
    this.alertService.dismissFreebiePrompt(candidatoId).subscribe({
      next: () => this.goHome(),
      error: () => this.goHome()
    });
  }

  private checkCvAndProceed() {
    const candidatoId = this.getCandidatoId();
    if (!candidatoId) {
      this.router.navigate(['/acceder']);
      return;
    }
    this.cvService.list(candidatoId).subscribe({
      next: (res: any) => {
        const cvs: any[] = res?.data || res || [];
        const hasActiveCv = cvs.some((cv: any) => cv.isActive === true);
        if (hasActiveCv) {
          this.triggerMatch();
        } else {
          this.cvUploadError = '';
          this.step = 'cv';
        }
      },
      error: () => {
        this.triggerMatch();
      }
    });
  }

  onClickUpload() {
    // Verificar consentimiento primero — igual que cv-vault
    if (this.consent.hasActiveSync() !== true) {
      this.consent.requestModal('cargar_cv');
      return;
    }
    this.fileInputRef.nativeElement.click();
  }

  onPickCv(event: any) {
    const file: File = event?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      this.cvUploadError = 'Formato no soportado. Usa PDF, DOC o DOCX.';
      return;
    }

    this.cvUploadError = '';
    this.uploadingCv = true;

    const candidatoId = this.getCandidatoId();
    this.cvService.upload(candidatoId, file, file.name).subscribe({
      next: (res: any) => {
        // Activate the uploaded CV
        const cv = res?.data || res;
        if (cv?.id) {
          this.cvService.activate(candidatoId, cv.id).subscribe({
            next: () => {
              this.uploadingCv = false;
              this.triggerMatch();
            },
            error: () => {
              this.uploadingCv = false;
              this.triggerMatch();
            }
          });
        } else {
          this.uploadingCv = false;
          this.triggerMatch();
        }
      },
      error: (err) => {
        this.uploadingCv = false;
        this.cvUploadError = err?.error?.message || err?.message || 'Error al subir CV';
      }
    });
  }

  private triggerMatch() {
    const active = this.consent.hasActiveSync();
    if (active === true) {
      this.runMatch();
      return;
    }
    if (active === null) {
      // El sync inicial de consent aún no resolvió — esperar el primer valor real.
      const sub = this.consent.changes().subscribe(val => {
        if (val === null) return;
        sub.unsubscribe();
        if (val === true) { this.runMatch(); }
        else { this.consent.requestModal('ia'); this.waitForConsentThenMatch(); }
      });
      return;
    }
    this.consent.requestModal('ia');
    this.waitForConsentThenMatch();
  }

  private waitForConsentThenMatch(): void {
    const sub = this.consent.changes().subscribe(active => {
      if (active === true) {
        sub.unsubscribe();
        this.runMatch();
      }
    });
  }

  private runMatch() {
    this.step = 'processing';
    this.scrapedJobService.freeOnboarding().subscribe({
      next: (res: any) => {
        const data = res?.data || res;
        const rawMatches = data?.matches || [];
        if (rawMatches.length === 0) {
          this.setError('No encontramos vacantes compatibles para ti en estos días. Intenta de nuevo más tarde.');
          return;
        }
        this.matches = rawMatches.map((m: any) => ({
          score: m.score || 0,
          titulo: m.titulo || '',
          empresa: m.empresa || '',
          ubicacion: m.ubicacion || '',
          modalidad: m.modalidad || '',
          urlVacante: m.urlVacante || ''
        }));
        this.sent = data?.sent === true;
        this.channel = data?.channel || 'NONE';
        this.step = 'results';
      },
      error: (err) => {
        const msg = err?.error?.message || err?.message || err.toString();
        this.setError(msg);
      }
    });
  }

  private setError(msg: string) {
    if (msg.includes('Ya has usado') || msg.includes('CONFLICT')) {
      this.errorTitle = 'Ya usaste tu GuruMatch gratis';
      this.errorMsg = 'Tu GuruMatch gratuito de bienvenida ya fue utilizado.';
      this.errorIcon = '✓';
    } else if (msg.includes('deshabilitado') || msg.includes('FORBIDDEN')) {
      this.errorTitle = 'No disponible por ahora';
      this.errorMsg = 'Esta funcionalidad está deshabilitada temporalmente.';
      this.errorIcon = 'ℹ';
    } else if (msg.includes('No encontramos vacantes')) {
      this.errorTitle = 'Sin resultados';
      this.errorMsg = 'No encontramos vacantes compatibles en estos días. Intenta de nuevo más tarde.';
      this.errorIcon = '∅';
    } else {
      this.errorTitle = 'Algo salió mal';
      this.errorMsg = msg;
      this.errorIcon = '✕';
    }
    this.step = 'error';
  }

  getScoreBg(score: number): string {
    if (score >= 80) return '#dcfce7';
    if (score >= 60) return '#fef9c3';
    return '#fee2e2';
  }

  getScoreColor(score: number): string {
    if (score >= 80) return '#166534';
    if (score >= 60) return '#854d0e';
    return '#991b1b';
  }

  getLocationText(m: MatchItem): string {
    return [m.ubicacion, m.modalidad].filter(s => s).join(' · ');
  }

  goHome() {
    this.router.navigate(['/']);
  }

  goPlans() {
    this.router.navigate(['/mis-suscripciones']);
  }

  viewJob(url: string) {
    if (!url) return;
    const href = url.startsWith('http') ? url : `https://tg-empleos.tgconsulting.online/vacantes/detalles/${url}`;
    window.open(href, '_blank');
  }
}
